import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toastError } from '../../../shared/toastHelper';
import { EventCard } from './MobileEventList';
import MobileCalendarDropdown, { buildYearOptions } from './MobileCalendarDropdown';
import '../styles/mobileFullCalendar.css';
import {
  SUNDAY_WEEKDAYS_LONG,
  MONDAY_WEEKDAYS_LONG,
  MONTH_NAMES,
  getDisplayRange,
  toISODateString,
  isSameDay,
  savedMonthStart,
  useSaveMonthOnChange,
  useGoToDate
} from './mobileCalendarHelpers';

const MobileFullCalendar = ({
  team,
  dataSource,
  filters,
  searchTerm,
  selectedRooms,
  weekStartDay,
  goToTodaySignal,
  goToDate,
  refreshSignal,
  initialMonth,
  onMonthChange
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => savedMonthStart(initialMonth, new Date()));
  useSaveMonthOnChange(currentMonth, onMonthChange);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Recompute today whenever the user navigates months — covers the
  // open-past-midnight case the previous mount-only memo missed.
  const today = useMemo(() => new Date(), [currentMonth]);

  useEffect(() => {
    if (!goToTodaySignal) return;
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  }, [goToTodaySignal]);

  const { displayStart, displayEnd } = useMemo(
    () => getDisplayRange(currentMonth, weekStartDay),
    [currentMonth, weekStartDay]
  );

  const weekdayLabels = weekStartDay === 1 ? MONDAY_WEEKDAYS_LONG : SUNDAY_WEEKDAYS_LONG;

  // Depend on stable scalar keys, not the filters/selectedRooms object
  // references, so the effect doesn't refire on every parent re-render.
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const selectedRoomIds = useMemo(
    () => (selectedRooms || []).map(r => r.value),
    [selectedRooms]
  );
  const selectedRoomIdsKey = selectedRoomIds.join(',');

  const latestRequestId = useRef(0);

  const fetchEvents = useCallback(async () => {
    const requestId = ++latestRequestId.current;
    const roomIds = filters.room_ids || selectedRoomIds;
    if (roomIds.length === 0) {
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = {
        ...filters,
        room_ids: roomIds,
        start: 0,
        length: 500,
        start_date: toISODateString(displayStart),
        end_date: toISODateString(displayEnd),
        'search[value]': searchTerm || '',
        mobile: true
      };

      const mobileUrl = dataSource.replace('/datatable', '/mobile_datatable');
      const response = await axios.get(mobileUrl, {
        params,
        headers: { 'Accept': 'application/json' }
      });
      if (requestId !== latestRequestId.current) return;
      setEvents(response.data.data || []);
    } catch (error) {
      if (requestId !== latestRequestId.current) return;
      console.error('Error fetching full calendar events:', error);
      toastError('Failed to load events. Please try again.');
      setEvents([]);
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, filtersKey, selectedRoomIdsKey, searchTerm, displayStart, displayEnd]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (refreshSignal) fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  useGoToDate(goToDate, (date) => setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1)));

  const eventsByDate = useMemo(() => {
    const grouped = {};
    events.forEach(event => {
      const key = event.start_date_iso;
      if (!key) return;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(event);
    });
    return grouped;
  }, [events]);

  const calendarDays = useMemo(() => {
    const days = [];
    const cursor = new Date(displayStart);
    while (cursor <= displayEnd) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [displayStart, displayEnd]);

  const weeks = useMemo(() => {
    const result = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const handleMonthSelect = (monthIdx) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIdx, 1));
  };

  const handleYearSelect = (year) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
  };

  const monthOptions = useMemo(
    () => MONTH_NAMES.map((name, idx) => ({ value: idx, label: name })),
    []
  );

  const yearOptions = useMemo(() => buildYearOptions(today.getFullYear()), [today]);

  return (
    <div className="mobile-full-calendar">
      <div className="mobile-full-calendar-toolbar">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          aria-label="Previous month"
          className="mobile-full-calendar-toolbar__nav mobile-full-calendar-toolbar__nav--left"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M15.8333 10H4.16667M4.16667 10L10 15.8333M4.16667 10L10 4.16667" stroke="#414651" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="mobile-full-calendar-toolbar__center">
          <MobileCalendarDropdown
            label={MONTH_NAMES[currentMonth.getMonth()]}
            ariaLabel="Select month"
            options={monthOptions}
            selectedValue={currentMonth.getMonth()}
            onSelect={handleMonthSelect}
          />
          <MobileCalendarDropdown
            label={String(currentMonth.getFullYear())}
            ariaLabel="Select year"
            options={yearOptions}
            selectedValue={currentMonth.getFullYear()}
            onSelect={handleYearSelect}
            width="110px"
          />
        </div>
        <button
          type="button"
          onClick={() => navigateMonth(1)}
          aria-label="Next month"
          className="mobile-full-calendar-toolbar__nav"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M4.16666 10H15.8333M15.8333 10L9.99999 4.16667M15.8333 10L9.99999 15.8333" stroke="#414651" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      <div className="mobile-full-calendar-scroll">
        <div className="mobile-full-calendar-canvas">
          <div className="mobile-full-calendar-weekdays">
            {weekdayLabels.map(label => (
              <div key={label} className="mobile-full-calendar-weekday">
                {label}
              </div>
            ))}
          </div>

          {loading && events.length === 0 ? (
            <div className="mobile-full-calendar-loading">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="mobile-full-calendar-grid">
              {weeks.map((week, weekIdx) => (
                <div key={weekIdx} className="mobile-full-calendar-week">
                  {week.map((day) => {
                    const dateKey = toISODateString(day);
                    const dayEvents = eventsByDate[dateKey] || [];
                    const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                    const dayIsToday = isSameDay(day, today);
                    const cellClass = `mobile-full-calendar-cell${isCurrentMonth ? '' : ' mobile-full-calendar-cell--out-of-month'}`;
                    const dateClass = `mobile-full-calendar-cell__date${dayIsToday ? ' mobile-full-calendar-cell__date--today' : ''}`;

                    return (
                      <div key={dateKey} className={cellClass}>
                        <div className={dateClass}>{day.getDate()}</div>
                        {dayEvents.map((event, idx) => (
                          <EventCard
                            key={event.id || idx}
                            event={event}
                            team={team}
                            stacked
                            dayEvents={dayEvents}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

MobileFullCalendar.propTypes = {
  team: PropTypes.object.isRequired,
  dataSource: PropTypes.string.isRequired,
  filters: PropTypes.object,
  searchTerm: PropTypes.string,
  selectedRooms: PropTypes.array,
  weekStartDay: PropTypes.number,
  goToTodaySignal: PropTypes.number,
  goToDate: PropTypes.shape({ date: PropTypes.string.isRequired }),
  refreshSignal: PropTypes.number,
  initialMonth: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onMonthChange: PropTypes.func
};

MobileFullCalendar.defaultProps = {
  filters: {},
  searchTerm: '',
  selectedRooms: [],
  weekStartDay: 0,
  goToTodaySignal: 0,
  goToDate: null,
  refreshSignal: 0
};

export default MobileFullCalendar;
